import { useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { InvitedUser, Permission, UserReadAll } from "store/api/generatedApi";
import { useTenantTranslation } from "hooks/formatters";
import { DrawerLayout } from "components/common/panels/layout";
import {
  CLARK_USER_PERMISSION,
  PERMISSION_VALUES,
} from "components/users/permissions.ts";
import { UserDrawerTarget } from "components/users/userAdminFilters";
import {
  ClarkProfileSection,
  useClarkProfileDraft,
} from "components/users/clarkProfileSection";
import { userDisplayName } from "src/utils/userDisplay";

type UserSubmit = (update: {
  uid: string;
  is_admin?: boolean;
  is_enabled: boolean;
  permissions: Permission[];
}) => Promise<void>;

const drawerContentSx = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  gap: 2,
} as const;

const DrawerActions = ({
  onCancel,
  onSave,
  saving,
  canSave,
}: {
  onCancel: () => void;
  onSave: () => Promise<void>;
  saving: boolean;
  canSave: boolean;
}) => {
  const { t } = useTenantTranslation();
  return (
    <Stack
      direction="row"
      sx={{
        position: "sticky",
        bottom: 0,
        bgcolor: "background.paper",
        pt: 1,
        display: "flex",
        justifyContent: "flex-end",
        gap: 1,
      }}
    >
      <Button onClick={onCancel}>{t("cancel")}</Button>
      <Button
        variant="contained"
        loading={saving}
        disabled={!canSave}
        onClick={() => void onSave()}
      >
        {t("save")}
      </Button>
    </Stack>
  );
};

const UserEditor = ({
  tenant,
  user,
  onClose,
  onSubmit,
  saving,
}: {
  tenant: string;
  user: UserReadAll;
  onClose: () => void;
  onSubmit: UserSubmit;
  saving: boolean;
}) => {
  const { t } = useTenantTranslation();

  const [isAdmin, setIsAdmin] = useState(user.is_admin);
  const [isEnabled, setIsEnabled] = useState(user.is_enabled);
  const [selected, setSelected] = useState(
    () => new Set<Permission>(user.permissions.map((entry) => entry.permission))
  );
  const [search, setSearch] = useState("");
  const isPerson = user.account_type === "user";
  const grantsClarkUser =
    isPerson &&
    selected.has(CLARK_USER_PERMISSION) &&
    !user.permissions.some(
      (entry) => entry.permission === CLARK_USER_PERMISSION
    );
  const clarkProfile = useClarkProfileDraft({
    tenant,
    email: isPerson ? user.email : null,
    fallbackName: user.name,
    grantsClarkUser,
  });

  const togglePermission = (permission: Permission) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(permission)) {
        next.delete(permission);
      } else {
        next.add(permission);
      }
      return next;
    });
  };

  const normalisedSearch = search.toLowerCase();
  const visiblePermissions = PERMISSION_VALUES.filter((permission) =>
    permission.toLowerCase().includes(normalisedSearch)
  );

  const submitPermissions = async () => {
    try {
      await onSubmit({
        uid: user.uid,
        ...(isAdmin === user.is_admin ? {} : { is_admin: isAdmin }),
        is_enabled: isEnabled,
        permissions: [...selected],
      });
      return true;
    } catch {
      setIsAdmin(user.is_admin);
      setIsEnabled(user.is_enabled);
      setSelected(new Set(user.permissions.map((entry) => entry.permission)));
      return false;
    }
  };

  const saveClarkProfile = async () => {
    if (!clarkProfile.isSaveDue) {
      return true;
    }
    try {
      await clarkProfile.save();
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = async () => {
    const isSaved = grantsClarkUser
      ? (await saveClarkProfile()) && (await submitPermissions())
      : (await submitPermissions()) && (await saveClarkProfile());
    if (isSaved) {
      onClose();
    }
  };

  return (
    <Stack sx={drawerContentSx}>
      <Typography variant="body2" color="text.secondary">
        {user.email}
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <Tooltip title={t("adminAppWideWarning")} describeChild>
          <FormControlLabel
            control={
              <Switch
                checked={isAdmin}
                onChange={(_, value) => {
                  setIsAdmin(value);
                }}
              />
            }
            label={t("admin")}
          />
        </Tooltip>
        <Tooltip title={t("enabledAppWideWarning")}>
          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={(_, value) => {
                  setIsEnabled(value);
                }}
              />
            }
            label={t("enabled")}
          />
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder={t("filterPermissions")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <Button
          size="small"
          onClick={() => {
            setSelected(
              (current) => new Set([...current, ...visiblePermissions])
            );
          }}
        >
          {t("selectAllPermissions")}
        </Button>
        <Button
          size="small"
          onClick={() => {
            setSelected(
              (current) =>
                new Set(
                  [...current].filter(
                    (permission) => !visiblePermissions.includes(permission)
                  )
                )
            );
          }}
        >
          {t("clearAllPermissions")}
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {t("selectedOfTotal", {
          selected: selected.size,
          total: PERMISSION_VALUES.length,
        })}
      </Typography>

      <Stack sx={{ flex: 1, minHeight: 240, overflowY: "auto" }}>
        {visiblePermissions.map((permission) => (
          <FormControlLabel
            key={permission}
            control={
              <Checkbox
                size="small"
                checked={selected.has(permission)}
                onChange={() => {
                  togglePermission(permission);
                }}
              />
            }
            label={permission}
          />
        ))}
      </Stack>

      {isPerson ? (
        <ClarkProfileSection draft={clarkProfile} email={user.email} />
      ) : null}

      <DrawerActions
        onCancel={onClose}
        onSave={handleSave}
        saving={saving || clarkProfile.isSaving}
        canSave={clarkProfile.canSave}
      />
    </Stack>
  );
};

const InviteEditor = ({
  tenant,
  invite,
  onClose,
}: {
  tenant: string;
  invite: InvitedUser;
  onClose: () => void;
}) => {
  const clarkProfile = useClarkProfileDraft({
    tenant,
    email: invite.email,
    fallbackName: "",
    grantsClarkUser: false,
  });

  const handleSave = async () => {
    if (clarkProfile.isSaveDue) {
      try {
        await clarkProfile.save();
      } catch {
        return;
      }
    }
    onClose();
  };

  return (
    <Stack sx={drawerContentSx}>
      <ClarkProfileSection draft={clarkProfile} email={invite.email} />
      <DrawerActions
        onCancel={onClose}
        onSave={handleSave}
        saving={clarkProfile.isSaving}
        canSave={clarkProfile.canSave}
      />
    </Stack>
  );
};

const drawerTitle = (target: UserDrawerTarget) =>
  target.kind === "user" ? userDisplayName(target.user) : target.invite.email;

export const UserDrawer = ({
  tenant,
  target,
  onClose,
  onSubmit,
  saving,
}: {
  tenant: string;
  target: UserDrawerTarget | null;
  onClose: () => void;
  onSubmit: UserSubmit;
  saving: boolean;
}) => {
  const { t } = useTenantTranslation();
  return (
    <DrawerLayout
      open={target !== null}
      doClose={onClose}
      title={target === null ? t("editUser") : drawerTitle(target)}
      width={400}
    >
      {target?.kind === "user" ? (
        <UserEditor
          tenant={tenant}
          user={target.user}
          onClose={onClose}
          onSubmit={onSubmit}
          saving={saving}
        />
      ) : null}
      {target?.kind === "invite" ? (
        <InviteEditor
          tenant={tenant}
          invite={target.invite}
          onClose={onClose}
        />
      ) : null}
    </DrawerLayout>
  );
};
